import { useEffect, useState } from "react";
import { apiCall } from "../api";
import { usePromo } from "../utils/promo";
import { Logo } from "./Logo";
import { FounderBanner } from "./FounderBanner";
import { GuestTenantSearch } from "./GuestTenantSearch";
import { BrandBackdrop, SampleReport, Tagline } from "./AuthLayout";

// Public counters only appear once they look solid
const MIN_RATINGS_TO_SHOW_STATS = 50;

const Icon = ({ d, className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d={d} />
  </svg>
);

const ICONS = {
  search: "M21 21l-5.2-5.2M17 10.5a6.5 6.5 0 11-13 0 6.5 6.5 0 0113 0z",
  shield: "M9 12l2 2 4-4m5.6-4A12 12 0 0112 3a12 12 0 01-8.6 3A12 12 0 003 9c0 5.6 3.8 10.3 9 11.6 5.2-1.3 9-6 9-11.6 0-1-.1-2-.4-3z",
  lock: "M16.5 10.5V7a4.5 4.5 0 10-9 0v3.5M6 10.5h12a1.5 1.5 0 011.5 1.5v7.5A1.5 1.5 0 0118 21H6a1.5 1.5 0 01-1.5-1.5V12A1.5 1.5 0 016 10.5z",
  star: "M11.5 3.5l2.4 4.9 5.4.8-3.9 3.8.9 5.4-4.8-2.6-4.8 2.6.9-5.4L3.7 9.2l5.4-.8 2.4-4.9z",
  money: "M3 7.5h18v9H3zM12 14.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5zM6 10v4M18 10v4",
  file: "M9 12h6m-6 4h6M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8l-5-5zm0 0v5h5",
  bell: "M15 17h5l-1.4-1.4A2 2 0 0118 14.2V11a6 6 0 00-4-5.7V5a2 2 0 10-4 0v.3A6 6 0 006 11v3.2a2 2 0 01-.6 1.4L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9",
  alert: "M12 9v4m0 4h.01M10.3 3.9L2.4 17.5A2 2 0 004.1 20.5h15.8a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z",
  chart: "M4 20V10m6 10V4m6 16v-7m4 7H2",
};

const STEPS = [
  { icon: "search", title: "Busca", text: "Escribe el nombre completo, correo o teléfono del interesado. La búsqueda es gratis y sin cuenta." },
  { icon: "file", title: "Revisa su historial", text: "Ve cómo pagó, cómo cuidó el inmueble y si cumplió el contrato con otros propietarios." },
  { icon: "star", title: "Evalúa a tus inquilinos", text: "Al terminar una renta, deja tu evaluación. Así cada propietario protege al siguiente." },
];

const PAINS = [
  { icon: "money", title: "Meses sin cobrar", text: "Cada mes de renta que no llega sale de tu bolsillo, y recuperarlo casi nunca es r\u00e1pido." },
  { icon: "file", title: "Procesos largos", text: "Sacar a un inquilino que no paga puede llevar meses de tr\u00e1mites, abogados y estr\u00e9s." },
  { icon: "alert", title: "Da\u00f1os al inmueble", text: "Paredes, muebles, adeudos de servicios: lo que no ves al firmar lo pagas al final." },
];

const REPORT_POINTS = [
  ["Sem\u00e1foro de riesgo", "Bajo, medio o alto, calculado con sus evaluaciones. El historial de pagos pesa m\u00e1s."],
  ["Calificaciones por categor\u00eda", "Pagos, cuidado del inmueble, comunicaci\u00f3n y cumplimiento del contrato."],
  ["Comentarios y evidencia", "Lo que vivieron otros propietarios, con documentos cuando los hay."],
];

const BENEFITS = [
  { icon: "money", title: "Evita rentas impagas", text: "Un mes sin cobrar cuesta más que cualquier consulta. Decide con información, no con corazonadas." },
  { icon: "shield", title: "Propietarios verificados", text: "Para evaluar, cada propietario confirma su identidad con identificación oficial y selfie." },
  { icon: "chart", title: "Semáforo de riesgo", text: "Cada inquilino muestra un nivel de riesgo bajo, medio o alto según su historial de pagos y su calificación." },
  { icon: "lock", title: "Información protegida", text: "Las consultas sin cuenta solo indican si existen evaluaciones. El detalle es solo para propietarios registrados." },
];

const Faq = ({ promo }) => {
  const items = [
    {
      q: "¿Cuánto cuesta?",
      a: promo.free_mode
        ? `Buscar siempre es gratis. Evaluar cuesta $${promo.rating_price_usd} USD y ver un reporte completo $${promo.report_price_usd} USD, pero con el Acceso Fundador no pagas nada por tiempo limitado.`
        : `Buscar es gratis. Evaluar a un inquilino cuesta $${promo.rating_price_usd} USD y desbloquear su reporte completo $${promo.report_price_usd} USD. Sin mensualidades.`,
    },
    {
      q: "¿Quién puede evaluar a un inquilino?",
      a: "Solo propietarios registrados que enviaron su identificación oficial y una selfie. Revisamos cada verificación y rechazamos las que no coinciden.",
    },
    {
      q: "¿Qué información ve un propietario?",
      a: "Calificaciones de pago, cuidado del inmueble, comunicación y cumplimiento del contrato, comentarios del propietario y, si los hay, documentos de evidencia.",
    },
    {
      q: "¿Cómo se calcula el semáforo de riesgo?",
      a: "Con el promedio de las evaluaciones del inquilino. El historial de pagos pesa más: si suele pagar tarde, su riesgo sube aunque su calificación general sea buena.",
    },
    {
      q: "¿Qué pasa si una evaluación es incorrecta?",
      a: "La persona evaluada puede pedir una revisión desde el enlace al pie de la página. Mientras tanto, la evaluación aparece marcada como \"En disputa\".",
    },
    {
      q: "¿Mis datos están seguros?",
      a: "Sí. Las contraseñas se guardan cifradas, la información viaja por conexión segura y tratamos los datos conforme a nuestro Aviso de Privacidad.",
    },
  ];
  return (
    <div className="divide-y divide-amber-200 border-y border-amber-200">
      {items.map(({ q, a }) => (
        <details key={q} className="group py-4">
          <summary className="flex justify-between items-center gap-4 cursor-pointer list-none font-semibold text-amber-900">
            {q}
            <span className="text-amber-700 text-xl leading-none transition-transform group-open:rotate-45" aria-hidden="true">+</span>
          </summary>
          <p className="mt-3 text-amber-800/80 leading-relaxed">{a}</p>
        </details>
      ))}
    </div>
  );
};

const Landing = ({ setAuthMode }) => {
  const promo = usePromo();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    apiCall('/public/stats').then(setStats).catch(() => {});
  }, []);

  const goRegister = () => setAuthMode('register');
  const goLogin = () => setAuthMode('login');
  const showStats = stats && stats.total_ratings >= MIN_RATINGS_TO_SHOW_STATS;
  const ctaLabel = promo.free_mode ? 'Obtener acceso fundador' : 'Crear cuenta gratis';

  return (
    <div className="min-h-screen bg-white text-amber-950 text-left">
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-amber-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Logo />
          <nav className="flex items-center gap-1 sm:gap-6 text-sm font-semibold">
            <a href="#como-funciona" className="hidden md:inline text-amber-900/70 hover:text-amber-950">Cómo funciona</a>
            <a href="#reporte" className="hidden md:inline text-amber-900/70 hover:text-amber-950">El reporte</a>
            <a href="#preguntas" className="hidden md:inline text-amber-900/70 hover:text-amber-950">Preguntas</a>
            <button onClick={goLogin} className="text-amber-900 hover:text-amber-700 px-2 py-2">Iniciar sesión</button>
            <button onClick={goRegister} className="hidden sm:inline-block bg-amber-950 text-amber-50 px-4 py-2 rounded-full hover:bg-amber-900 shadow-sm">Crear cuenta</button>
          </nav>
        </div>
      </header>

      <FounderBanner onCta={goRegister} />

      {/* Hero: the tagline on amber, the search right next to it */}
      <section className="relative overflow-hidden bg-gradient-to-br from-amber-500 via-amber-600 to-amber-800 text-white">
        <BrandBackdrop />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-12 sm:pt-20 pb-16 sm:pb-24 grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-14 items-center">
          <div>
            <p className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold text-amber-50 bg-white/15 ring-1 ring-white/25 rounded-full px-3 py-1 mb-6 backdrop-blur">
              <Icon d={ICONS.shield} className="w-4 h-4" /> Historial de inquilinos hecho por propietarios
            </p>
            <Tagline className="text-4xl sm:text-5xl lg:text-6xl" />
            <p className="mt-6 text-lg sm:text-xl text-amber-50/95 leading-relaxed max-w-xl">
              Antes de entregar las llaves, descubre si otros propietarios ya le rentaron, c&oacute;mo pag&oacute; y c&oacute;mo dej&oacute; el inmueble.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button onClick={goRegister} className="bg-white text-amber-800 font-bold px-7 py-3.5 rounded-full hover:bg-amber-50 shadow-xl shadow-amber-950/20">{ctaLabel}</button>
              <a href="#reporte" className="text-center border-2 border-white/60 text-white font-semibold px-7 py-3 rounded-full hover:bg-white/10">Ver un reporte de ejemplo</a>
            </div>
            <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-amber-50">
              {["Búsqueda gratis y sin cuenta", "Propietarios verificados", "Semáforo de riesgo"].map(t => (
                <li key={t} className="flex items-center gap-1.5">
                  <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 111.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" clipRule="evenodd" /></svg>
                  {t}
                </li>
              ))}
            </ul>
          </div>
          <div id="buscar" className="scroll-mt-24 text-amber-950">
            <GuestTenantSearch onRegister={goRegister} />
          </div>
        </div>
        {/* Curved bottom edge */}
        <svg className="relative block w-full h-10 sm:h-16 text-white" viewBox="0 0 1440 80" preserveAspectRatio="none" aria-hidden="true">
          <path fill="currentColor" d="M0 80h1440V20C1200 70 960 80 720 60S240 0 0 30z" />
        </svg>
      </section>

      {showStats && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 -mt-2 pb-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
            {[
              [stats.total_ratings, 'evaluaciones registradas'],
              [stats.total_tenants, 'inquilinos en el historial'],
              [stats.verified_landlords, 'propietarios verificados'],
            ].map(([n, label]) => (
              <div key={label} className="bg-white border border-amber-100 rounded-2xl py-6 shadow-lg shadow-amber-100/60">
                <p className="text-4xl font-extrabold bg-gradient-to-r from-amber-600 to-amber-800 bg-clip-text text-transparent">{n.toLocaleString('es-MX')}</p>
                <p className="text-sm text-amber-900/70 mt-1">{label}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* The problem: why it matters */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <p className="text-center text-amber-700 font-bold uppercase tracking-[0.18em] text-xs mb-3">El problema</p>
        <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-center max-w-3xl mx-auto">Un mal inquilino no avisa. Su historial s&iacute;.</h2>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {PAINS.map(p => (
            <div key={p.title} className="relative rounded-3xl p-7 bg-gradient-to-b from-amber-50 to-white border border-amber-100">
              <div className="w-12 h-12 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center mb-5 ring-1 ring-red-100">
                <Icon d={ICONS[p.icon]} />
              </div>
              <h3 className="text-lg font-bold">{p.title}</h3>
              <p className="mt-2 text-amber-900/75 leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="como-funciona" className="bg-amber-950 text-amber-50 scroll-mt-16 relative overflow-hidden">
        <div className="pointer-events-none absolute -top-40 right-0 w-[32rem] h-[32rem] rounded-full bg-amber-600/25 blur-3xl" aria-hidden="true" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <p className="text-amber-400 font-bold uppercase tracking-[0.18em] text-xs mb-3">C&oacute;mo funciona</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight max-w-2xl text-white">Tres pasos para rentar con tranquilidad</h2>
          <ol className="mt-12 grid md:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <li key={s.title} className="relative rounded-3xl p-7 bg-white/[0.06] ring-1 ring-white/10 backdrop-blur">
                <span className="absolute top-5 right-6 text-6xl font-extrabold text-white/10 leading-none select-none" aria-hidden="true">0{i + 1}</span>
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 text-white flex items-center justify-center shadow-lg shadow-amber-900/40 mb-5">
                  <Icon d={ICONS[s.icon]} />
                </div>
                <h3 className="text-xl font-bold text-white">{s.title}</h3>
                <p className="mt-2 text-amber-100/80 leading-relaxed">{s.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Sample report */}
      <section id="reporte" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24 scroll-mt-16 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className="text-amber-700 font-bold uppercase tracking-[0.18em] text-xs mb-3">El reporte</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Todo lo que necesitas saber, en un vistazo</h2>
          <p className="mt-4 text-lg text-amber-900/75 leading-relaxed">Cada inquilino tiene un sem&aacute;foro de riesgo, calificaciones por categor&iacute;a y los comentarios de quienes ya le rentaron.</p>
          <ul className="mt-8 space-y-4">
            {REPORT_POINTS.map(([title, text]) => (
              <li key={title} className="flex gap-4">
                <span className="mt-1 w-6 h-6 rounded-full bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
                  <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 111.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" clipRule="evenodd" /></svg>
                </span>
                <div><p className="font-bold">{title}</p><p className="text-amber-900/70">{text}</p></div>
              </li>
            ))}
          </ul>
        </div>
        <div className="relative flex justify-center py-6">
          <div className="absolute inset-4 sm:inset-8 rounded-[2.5rem] bg-gradient-to-br from-amber-400 to-amber-700 rotate-3" aria-hidden="true" />
          <div className="relative py-6 px-2 sm:px-6"><SampleReport /></div>
        </div>
      </section>

      {/* Trust */}
      <section className="bg-gradient-to-b from-amber-50 to-white border-y border-amber-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <p className="text-center text-amber-700 font-bold uppercase tracking-[0.18em] text-xs mb-3">Confianza</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-center">Por qu&eacute; los propietarios conf&iacute;an en nosotros</h2>
          <div className="mt-12 grid sm:grid-cols-2 gap-6">
            {BENEFITS.map(b => (
              <div key={b.title} className="group bg-white border border-amber-100 rounded-3xl p-7 flex gap-5 shadow-sm hover:shadow-xl hover:shadow-amber-100 hover:-translate-y-0.5 transition-all">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 text-white flex items-center justify-center flex-shrink-0 shadow-md shadow-amber-200">
                  <Icon d={ICONS[b.icon]} />
                </div>
                <div>
                  <h3 className="text-lg font-bold">{b.title}</h3>
                  <p className="mt-1 text-amber-900/75 leading-relaxed">{b.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="preguntas" className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-20 scroll-mt-16">
        <p className="text-center text-amber-700 font-bold uppercase tracking-[0.18em] text-xs mb-3">Preguntas</p>
        <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-center mb-10">Preguntas frecuentes</h2>
        <Faq promo={promo} />
      </section>

      <section className="px-4 sm:px-6 pb-16 sm:pb-24">
        <div className="relative overflow-hidden max-w-5xl mx-auto bg-gradient-to-br from-amber-500 via-amber-600 to-amber-800 rounded-[2rem] px-6 py-14 sm:p-16 text-center shadow-2xl shadow-amber-300/50">
          <BrandBackdrop />
          <h2 className="relative text-3xl sm:text-4xl font-extrabold text-white tracking-tight">Tu pr&oacute;xima renta empieza con una buena decisi&oacute;n</h2>
          <p className="relative mt-4 text-amber-50 text-lg max-w-xl mx-auto">
            {promo.free_mode
              ? 'Únete como propietario fundador y evalúa y consulta sin costo por tiempo limitado.'
              : 'Crea tu cuenta en un minuto y consulta a tu próximo inquilino hoy.'}
          </p>
          <div className="relative mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={goRegister} className="bg-white text-amber-800 font-bold px-8 py-3.5 rounded-full hover:bg-amber-50 shadow-xl shadow-amber-950/20">{ctaLabel}</button>
            <a href="#buscar" className="border-2 border-white/60 text-white font-semibold px-8 py-3 rounded-full hover:bg-white/10">Buscar un inquilino</a>
          </div>
        </div>
      </section>
    </div>
  );
};

export { Landing };
